/**
 * Niederschlagsradar und Niederschlagsvorhersage von MeteoSchweiz über der Karte.
 *
 * Zwei Quellen, ein Zeitstrahl: die gemessenen Bilder der letzten Stunde
 * entstehen auf unserem Server alle fünf Minuten aus den RZC-Rohdaten
 * (deploy/radar-update.py), die gerechneten der nächsten Stunden stündlich aus
 * ICON-CH1 (deploy/prognose-radar.py). Beide liegen im selben Ausschnitt und
 * in derselben Farbskala, deshalb lassen sie sich ohne Bruch hintereinander
 * abspielen.
 *
 * Eine Bildquelle statt Kacheln: das Komposit ist ein einziges 1-km-Gitter über
 * den ganzen Alpenraum, da lohnt sich kein Kachelsatz. Weil das Bild nach Norden
 * ausgerichtet in Web-Mercator vorliegt, sind die vier Eckpunkte exakt - MapLibre
 * interpoliert zwischen ihnen in genau dieser Projektion.
 */
import { mitBasis } from '../data/basis.js';
const BASE = mitBasis('/radar');
const SOURCE = 'radar';
const LAYER = 'radar-layer';
/** Darüber beginnt die Beschriftung - die soll oben bleiben. */
const BEFORE = 'label-water';

const pad = (n) => String(n).padStart(2, '0');
const uhr = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** "20261002T1950" -> lokale Zeit */
function readStamp(key) {
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})$/.exec(key || '');
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  return new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi));
}

export function createRadar({ map, onZustand }) {
  let bilder = [];          // { key, zeit, art } nach Zeit sortiert
  let ecken = null;
  let lauf = null;          // Anfangszeit des Vorhersagelaufs
  let on = false;
  let pos = -1;             // Bildnummer, -1 = jüngste Messung
  let playing = false;
  let playTimer = null;
  let pollTimer = null;
  const nodes = {};
  const strahl = {};

  /** Die jüngste Messung - der Punkt, auf dem das Radar standardmässig steht. */
  function messungIndex() {
    for (let i = bilder.length - 1; i >= 0; i -= 1) {
      if (bilder[i].art === 'messung') return i;
    }
    return bilder.length - 1;
  }

  const aktuell = () => {
    if (!bilder.length) return null;
    const i = pos < 0 ? messungIndex() : Math.min(pos, bilder.length - 1);
    return bilder[i];
  };

  const bildUrl = (f) =>
    `${BASE}/${f.art === 'prognose' ? 'prognose' : 'frames'}/${f.key}.png`;

  async function holeJson(datei) {
    try {
      // Umgehungszwischenspeicher: die Bilder wechseln alle fünf Minuten.
      const r = await fetch(`${BASE}/${datei}?t=${Date.now()}`, { cache: 'no-store' });
      if (!r.ok) throw new Error(String(r.status));
      return await r.json();
    } catch {
      return null;
    }
  }

  async function loadIndex() {
    const [mess, prog] = await Promise.all([holeJson('index.json'), holeJson('prognose.json')]);
    const liste = [];
    for (const key of mess?.bilder || []) {
      const zeit = readStamp(key);
      if (zeit) liste.push({ key, zeit, art: 'messung' });
    }
    // Eine Vorhersagestunde, die schon vorbei ist, wird von der Messung
    // abgelöst - sie würde sonst doppelt im Strahl stehen.
    const jetzt = Date.now();
    for (const key of prog?.bilder || []) {
      const zeit = readStamp(key);
      if (zeit && zeit.getTime() > jetzt) liste.push({ key, zeit, art: 'prognose' });
    }
    liste.sort((a, b) => a.zeit - b.zeit);
    bilder = liste;
    ecken = mess?.ecken || prog?.ecken || null;
    lauf = readStamp(prog?.lauf);
    return bilder.length > 0;
  }

  /** Quelle und Ebene anlegen - auch nach einem Stilwechsel wieder. */
  function attach() {
    const f = aktuell();
    if (!on || !f || !ecken) return;
    if (!map.getSource(SOURCE)) {
      map.addSource(SOURCE, { type: 'image', url: bildUrl(f), coordinates: ecken });
    } else {
      map.getSource(SOURCE).updateImage({ url: bildUrl(f) });
    }
    if (!map.getLayer(LAYER)) {
      map.addLayer({
        id: LAYER,
        type: 'raster',
        source: SOURCE,
        // Weich statt stufig: die Daten sind schon beim Erzeugen zwischen den
        // Gitterzellen gemittelt, hier wird nur noch die Pixelkante geglaettet.
        paint: { 'raster-opacity': 0.82, 'raster-fade-duration': 0, 'raster-resampling': 'linear' }
      }, map.getLayer(BEFORE) ? BEFORE : undefined);
    }
    showCredit(true);
  }

  /**
   * CC-BY verlangt die Nennung. Eine Bildquelle nimmt in MapLibre kein
   * `attribution` - der Stil lehnt die Quelle sonst ganz ab. Darum wird der
   * Hinweis direkt in die Quellenangabe gehaengt, solange das Radar laeuft.
   */
  const CREDIT_ID = 'radar-credit';
  function showCredit(show) {
    const host = document.querySelector('.maplibregl-ctrl-attrib-inner');
    if (!host) return;
    const existing = host.querySelector(`#${CREDIT_ID}`);
    if (show && !existing) {
      const span = document.createElement('span');
      span.id = CREDIT_ID;
      span.innerHTML = ' | <a href="https://www.meteoschweiz.admin.ch" target="_blank" rel="noopener">MeteoSchweiz</a>';
      host.append(span);
    } else if (!show && existing) {
      existing.remove();
    }
  }

  function detach() {
    if (map.getLayer(LAYER)) map.removeLayer(LAYER);
    if (map.getSource(SOURCE)) map.removeSource(SOURCE);
  }

  function stopPlaying() {
    playing = false;
    clearInterval(playTimer);
    playTimer = null;
  }

  /**
   * Die Bilder vorab holen. Sonst läuft der Film dem Netz davon: das nächste Bild
   * wird angefordert, bevor das letzte da ist, und auf dem Telefon bleibt der
   * Ablauf stehen, statt zu laufen.
   */
  const vorgeladen = new Map();
  function vorladen() {
    for (const f of bilder) {
      if (vorgeladen.has(f.key)) continue;
      const i = new Image();
      i.src = bildUrl(f);
      vorgeladen.set(f.key, i);
    }
  }

  function startPlaying() {
    if (bilder.length < 2) return;
    vorladen();
    playing = true;
    pos = 0;
    clearInterval(playTimer);
    playTimer = setInterval(() => {
      // Im Hintergrund muss nichts abgespielt und nichts geladen werden.
      if (document.hidden) return;
      pos += 1;
      if (pos >= bilder.length) pos = 0;
      attach();
      render();
    }, 550);
  }

  function setPos(i) {
    pos = Math.max(0, Math.min(i, bilder.length - 1));
    attach();
    render();
  }

  async function setOn(next) {
    on = next;
    if (on) {
      // Jedes Mal neu laden, nicht nur beim ersten Mal: war das Radar eine
      // Weile aus, zeigt die alte Liste Bilder von damals - und der Strich
      // fuer jetzt stuende rechts ausserhalb des Strahls.
      await loadIndex();
      if (!bilder.length) { on = false; render(); return; }
      pos = -1;
      attach();
      // Alle zwei Minuten nachsehen, ob ein neueres Bild vorliegt.
      clearInterval(pollTimer);
      pollTimer = setInterval(async () => {
        if (document.hidden) return;
        const vorher = aktuell()?.key;
        await loadIndex();
        if (!playing && aktuell()?.key !== vorher) { pos = -1; attach(); }
        render();
      }, 120000);
    } else {
      stopPlaying();
      clearInterval(pollTimer);
      pollTimer = null;
      detach();
    }
    render();
  }

  /* ----------------------------------------------------------------
     Zeitstrahl über der Karte
     ---------------------------------------------------------------- */

  /** Wo ein Zeitpunkt auf dem Strahl liegt, in Prozent. Massstab ist die Zeit
   *  selbst, nicht die Bildnummer - sonst wären die fünf gemessenen Minuten
   *  genauso breit wie eine gerechnete Stunde. */
  function anteil(t) {
    if (bilder.length < 2) return 0;
    const a = bilder[0].zeit.getTime();
    const b = bilder[bilder.length - 1].zeit.getTime();
    return b === a ? 0 : ((t - a) / (b - a)) * 100;
  }

  /**
   * Eine Beschriftung auf den Strahl setzen.
   *
   * Mittig über ihrem Punkt - ausser am Rand: dort würde die halbe Schrift
   * über den Strahl hinauslaufen. Die Verschiebung wandert deshalb mit der
   * Lage von 0 auf -100 Prozent, ganz links steht die Marke also links
   * ausgerichtet, ganz rechts rechts.
   */
  function setzen(el, p) {
    const v = Math.max(0, Math.min(100, p));
    el.style.left = `${p}%`;
    el.style.transform = `translateX(${-v}%)`;
    // Wo der Zeitpunkt innerhalb der verschobenen Beschriftung liegt - daran
    // hängt der Strich darunter.
    el.style.setProperty('--innen', `${v}%`);
  }

  /** Das Bild, dessen Zeit einem Anteil am nächsten liegt. */
  function nachAnteil(p) {
    if (!bilder.length) return 0;
    const a = bilder[0].zeit.getTime();
    const b = bilder[bilder.length - 1].zeit.getTime();
    const ziel = a + ((b - a) * p) / 100;
    let best = 0;
    for (let i = 1; i < bilder.length; i += 1) {
      if (Math.abs(bilder[i].zeit - ziel) < Math.abs(bilder[best].zeit - ziel)) best = i;
    }
    return best;
  }

  function strahlBauen() {
    const wurzel = document.createElement('div');
    wurzel.className = 'zeitstrahl';
    wurzel.hidden = true;

    const play = document.createElement('button');
    play.type = 'button';
    play.className = 'zeitstrahl__play';
    play.setAttribute('aria-label', 'Ablauf abspielen');

    const achse = document.createElement('div');
    achse.className = 'zeitstrahl__achse';
    achse.tabIndex = 0;
    achse.setAttribute('role', 'slider');
    achse.setAttribute('aria-label', 'Zeitpunkt des Niederschlagsbilds');

    const zukunft = document.createElement('div');
    zukunft.className = 'zeitstrahl__zukunft';
    const marken = document.createElement('div');
    marken.className = 'zeitstrahl__marken';
    const jetztstrich = document.createElement('div');
    jetztstrich.className = 'zeitstrahl__jetzt';
    const griff = document.createElement('div');
    griff.className = 'zeitstrahl__griff';
    const fahne = document.createElement('div');
    fahne.className = 'zeitstrahl__fahne';

    achse.append(zukunft, marken, jetztstrich, griff, fahne);
    wurzel.append(play, achse);

    play.addEventListener('click', () => {
      if (playing) stopPlaying(); else startPlaying();
      render();
    });

    const greifen = (ev) => {
      const r = achse.getBoundingClientRect();
      const p = ((ev.clientX - r.left) / r.width) * 100;
      stopPlaying();
      setPos(nachAnteil(p));
    };
    achse.addEventListener('pointerdown', (ev) => {
      achse.setPointerCapture(ev.pointerId);
      greifen(ev);
    });
    achse.addEventListener('pointermove', (ev) => {
      if (achse.hasPointerCapture(ev.pointerId)) greifen(ev);
    });
    achse.addEventListener('keydown', (ev) => {
      const schritt = { ArrowLeft: -1, ArrowRight: 1, ArrowDown: -1, ArrowUp: 1 }[ev.key];
      if (!schritt) return;
      ev.preventDefault();
      stopPlaying();
      setPos((pos < 0 ? messungIndex() : pos) + schritt);
    });

    Object.assign(strahl, { wurzel, play, achse, zukunft, marken, jetztstrich, griff, fahne });
    return wurzel;
  }

  /** Volle Stunden beschriften - dazwischen wäre es auf dem Handy Kleingedrucktes. */
  function markenZeichnen() {
    strahl.marken.textContent = '';
    if (bilder.length < 2) return;
    const a = bilder[0].zeit;
    const b = bilder[bilder.length - 1].zeit;
    const t = new Date(a);
    t.setMinutes(0, 0, 0);
    if (t < a) t.setHours(t.getHours() + 1);
    while (t <= b) {
      const p = anteil(t.getTime());
      const m = document.createElement('span');
      m.className = 'zeitstrahl__marke';
      m.textContent = uhr(t);
      setzen(m, p);
      strahl.marken.append(m);
      t.setHours(t.getHours() + 1);
    }
  }

  function strahlZeichnen() {
    if (!strahl.wurzel) return;
    strahl.wurzel.hidden = !on || bilder.length < 2;
    if (strahl.wurzel.hidden) return;

    const f = aktuell();
    const letzteMessung = bilder[messungIndex()];
    const grenze = anteil(letzteMessung.zeit.getTime());
    strahl.zukunft.style.left = `${grenze}%`;
    strahl.zukunft.style.width = `${Math.max(0, 100 - grenze)}%`;

    const jetztP = anteil(Date.now());
    const sichtbar = jetztP >= 0 && jetztP <= 100;
    strahl.jetztstrich.hidden = !sichtbar;
    if (sichtbar) strahl.jetztstrich.style.left = `${jetztP}%`;

    const p = anteil(f.zeit.getTime());
    strahl.griff.style.left = `${p}%`;
    strahl.fahne.textContent = uhr(f.zeit);
    setzen(strahl.fahne, p);
    strahl.fahne.classList.toggle('zeitstrahl__fahne--prognose', f.art === 'prognose');
    strahl.play.classList.toggle('zeitstrahl__play--an', playing);
    strahl.play.setAttribute('aria-label', playing ? 'Anhalten' : 'Ablauf abspielen');

    strahl.achse.setAttribute('aria-valuetext',
      `${uhr(f.zeit)} Uhr, ${f.art === 'prognose' ? 'Vorhersage' : 'Messung'}`);
    markenZeichnen();
  }

  /* ---------------------------------------------------------------- */

  /** Der Satz in der Spalte: woher das gezeigte Bild stammt und wie alt es ist. */
  function lage() {
    const f = aktuell();
    if (!f) return 'nicht erreichbar';
    if (f.art === 'prognose') {
      const l = lauf ? `, gerechnet ${uhr(lauf)} Uhr` : '';
      return `Vorhersage für ${uhr(f.zeit)} Uhr${l}`;
    }
    const min = Math.round((Date.now() - f.zeit.getTime()) / 60000);
    if (min < 1) return `Messung ${uhr(f.zeit)} Uhr, soeben`;
    if (min < 60) return `Messung ${uhr(f.zeit)} Uhr, vor ${min} min`;
    return `Messung ${uhr(f.zeit)} Uhr`;
  }

  function render() {
    onZustand?.(on);
    strahlZeichnen();
    if (!nodes.root) return;
    nodes.toggle.checked = on;
    nodes.body.hidden = !on;
    if (!on) return;
    nodes.time.textContent = lage();
  }

  function mount(host) {
    if (!host) return;
    host.textContent = '';
    host.classList.add('radar');

    const row = document.createElement('label');
    row.className = 'check';
    const toggle = document.createElement('input');
    toggle.type = 'checkbox';
    const text = document.createElement('span');
    text.textContent = 'Niederschlagsradar';
    // Die Quelle steht schon in der Kopfzeile der Spalte - hier waere sie
    // dasselbe Wort ein zweites Mal.
    row.append(toggle, text);

    const body = document.createElement('div');
    body.className = 'radar__body';
    body.hidden = true;

    const time = document.createElement('div');
    time.className = 'radar__time';

    // Legende: die Farbskala und darunter, was sie bedeutet. Ohne die Zahlen
    // sind fuenf Farbfelder nur Dekoration.
    const legend = document.createElement('div');
    legend.className = 'radar__legend';
    const stufen = [[0.4, [102, 176, 230]], [1.6, [60, 160, 110]],
                    [6.3, [225, 225, 60]], [25, [235, 95, 40]], [63, [175, 30, 85]]];
    for (const [mm, rgb] of stufen) {
      const cell = document.createElement('span');
      cell.style.background = `rgb(${rgb.join(',')})`;
      cell.title = `bis ${mm} mm/h`;
      legend.append(cell);
    }
    const legendText = document.createElement('span');
    legendText.className = 'radar__legend-text';
    legendText.textContent = 'mm/h';
    legend.append(legendText);

    const skala = document.createElement('div');
    skala.className = 'radar__skala';
    for (const [mm] of stufen) {
      const z = document.createElement('span');
      // Komma, nicht Punkt - de-CH
      z.textContent = String(mm).replace('.', ',');
      skala.append(z);
    }

    body.append(time, legend, skala);
    host.append(row, body);
    Object.assign(nodes, { root: host, toggle, body, time });

    toggle.addEventListener('change', () => setOn(toggle.checked));

    // Der Zeitstrahl gehoert auf die Karte, nicht in die Spalte: er wird
    // gebraucht, waehrend man das Bild ansieht - und die Spalte ist dann oft zu.
    const karte = map.getContainer();
    if (karte && !strahl.wurzel) karte.append(strahlBauen());

    render();
  }

  return {
    mount,
    /** Nach einem Stilwechsel sind eigene Ebenen weg - wieder auftragen. */
    reapply: () => { if (on) attach(); },
    isOn: () => on
  };
}
